import React from 'react';
import InputLayout from '../InputLayout';
import Button from '@material-ui/core/Button';
import SelectLayout from '../SelectLayout';
import SelectCalendar from '../SelectCalendar';
import GraphHeader from './GraphHeader';
import {
  conditionList,
  countryList,
  marketList,
  value,
  graphEffect,
  getToday,
} from './Context';

interface Props {}

export type DataCondition = {
  condition: string;
  country: string;
  market: string;
  company: string | undefined;
  value: string | undefined;
  graphEffect: string | undefined;
};

export type CompanyValueData = {
  date: string | undefined;
  open: number;
  close: number;
  high: number;
  low: number;
  volume: number;
  companyName: string | null;
};

type Loading = {
  select: boolean;
  drawing: boolean;
};

const initialDataCondition: DataCondition = {
  condition: '',
  country: '',
  market: '',
  company: '',
  value: '',
  graphEffect: '',
};

const ChartMenu: React.FC<Props> = React.memo(() => {
  const [isSelect, setIsSelect] = React.useState<boolean>(false);
  const [dataCondition, setDataCondition] =
    React.useState<DataCondition>(initialDataCondition);
  const [company, setCompany] = React.useState<Array<any>>([]);
  const [companyList, setCompanyList] = React.useState<Array<string>>([]);
  const [companyStartDate, setCompanyStartDate] = React.useState<string>('');
  const [companyEndDate, setCompanyEndDate] = React.useState<string>('');
  const [dateRange, setDateRange] = React.useState<Array<Date | null>>([]);
  const [valueData, setValueData] = React.useState<Array<CompanyValueData>>([]);
  const [loading, setLoading] = React.useState<Loading>({
    select: false,
    drawing: false,
  });

  const getCompanyData = React.useCallback(async (market: string | null) => {
    setCompany([]);
    if (market !== null) {
      console.log('getCompanyData!');
      const toLowerMarket = market?.toLowerCase();
      const response = await fetch(`/api/stock/company/${toLowerMarket}`);
      const resJson = await response.json();

      if (!response.ok) {
        return window.alert('failed!');
      }
      setCompany(resJson);
    }
  }, []);
  const getCompanyListData = React.useCallback(
    async (market: string | null) => {
      setCompanyList([]);
      if (market !== null) {
        console.log('getCompanyListData!');
        const toLowerMarket = market?.toLowerCase();
        const response = await fetch(`/api/stock/companyname/${toLowerMarket}`);
        const resJson = await response.json();

        if (!response.ok) {
          return window.alert('failed!');
        }
        setCompanyList(resJson);
      }
    },
    [],
  );

  const getDateRange = React.useCallback(async (companyId: string) => {
    const response = await fetch(`/api/stock/data/${companyId}`);

    if (!response.ok) {
      setIsSelect(false);
      return window.alert('failed!');
    }

    const resJson = await response.json();
    setCompanyStartDate(resJson[0].date);
    setCompanyEndDate(resJson[1].date);
  }, []);

  const getDataValue = React.useCallback(
    async (companyName: string, start: string, end: string) => {
      setValueData([]);
      setLoading({
        select: false,
        drawing: true,
      });
      const response = await fetch(
        `/api/stock/data/condition/${companyName}?start=${start}&end=${end}`,
      );
      if (!response.ok) {
        setLoading({ drawing: false, select: false });
        return window.alert('failed!');
      }
      const resJson = await response.json();
      setValueData(resJson);
      setLoading({ select: true, drawing: false });
    },
    [],
  );

  const getValueHandler = (companyName: string, start: string, end: string) => {
    getDataValue(companyName, start, end);
  };

  return (
    <>
      <div className="md:mt-4">
        <div className="w-full grid gap-x-4 grid-cols-2 md:grid-cols-4 md:gap-8">
          <SelectLayout
            onChange={(e: any) => {
              setDataCondition({
                condition: e.target.value,
                country: '',
                market: '',
                company: '',
                value: '',
                graphEffect: '',
              });
              setIsSelect(false);
            }}
            id="condition"
            disabled={false}
            value={dataCondition.condition}
            label="CONDITION"
            data={conditionList}
          />
          <SelectLayout
            onChange={(e: any) => {
              setDataCondition(() => ({
                ...dataCondition,
                country: e.target.value,
                market: '',
                company: '',
                value: '',
              }));
              setIsSelect(false);
            }}
            value={dataCondition.country}
            id="country"
            disabled={dataCondition.condition === ''}
            label="COUNTRY"
            data={countryList}
          />
          <SelectLayout
            onChange={(e: any) => {
              setDataCondition(() => ({
                ...dataCondition,
                market: e.target.value,
                company: '',
                value: '',
              }));
              getCompanyData(e.target.value);
              getCompanyListData(e.target.value);
              setIsSelect(false);
            }}
            value={dataCondition.market}
            id="market"
            disabled={dataCondition.country === ''}
            label="MARKET"
            data={
              dataCondition.country === 'KOREA' ? marketList[0] : marketList[1]
            }
          />
          <InputLayout
            onChange={(e: any, newValue: string | undefined) => {
              setDataCondition(() => ({
                ...dataCondition,
                company: newValue,
                value: '',
              }));
              setIsSelect(false);
            }}
            value={dataCondition.company}
            id="company"
            disabled={dataCondition.market === ''}
            label="COMPANY"
            data={companyList}
          />
        </div>
        <Button
          disabled={dataCondition.company === ''}
          onClick={() => {
            const property: Array<{ companyId: string; companyName: string }> =
              company.filter((element) => {
                return element.companyName === dataCondition.company;
              });
            setIsSelect(true);
            getDateRange(property[0].companyId);
            setDateRange([]);
          }}>
          SELECT
        </Button>
        <div
          className={`${
            isSelect === true
              ? 'opacity-100 max-h-80'
              : 'opacity-0 max-h-0 pointer-events-none'
          } transition:max-height ease-in duration-500`}>
          <div className="w-full grid gap-x-4 grid-cols-2 md:grid-cols-4 md:gap-8">
            <SelectCalendar
              selectedDate={dateRange[0]}
              startDate={new Date(companyStartDate)}
              endDate={new Date(companyEndDate)}
              openToDate={companyStartDate}
              onChange={(date: Date) => setDateRange([date])}
              disabled={false}
            />
            <SelectCalendar
              selectedDate={dateRange[0] ? new Date() : dateRange[0]}
              startDate={
                new Date(dateRange[0] === null ? new Date() : dateRange[0])
              }
              endDate={new Date(companyEndDate)}
              openToDate={companyEndDate}
              onChange={(date: Date) =>
                setDateRange(() => [dateRange[0], date])
              }
              disabled={dateRange.length === 0 ? true : false}
            />
            <InputLayout
              onChange={(e: any, newValue: string | undefined) => {
                setDataCondition(() => ({
                  ...dataCondition,
                  value: newValue,
                }));
              }}
              value={dataCondition.value}
              id="value"
              disabled={dateRange.length === 2 ? false : true}
              label="VALUE"
              data={value}
            />
            <InputLayout
              onChange={(e: any, newValue: string | undefined) => {
                setDataCondition(() => ({
                  ...dataCondition,
                  graphEffect: newValue,
                }));
              }}
              value={dataCondition.graphEffect}
              id="nothing"
              disabled={dataCondition.value === ''}
              label="GRAPH TYPE"
              data={graphEffect}
            />
          </div>
          <Button
            disabled={dataCondition.graphEffect === '' ? true : false}
            onClick={() => {
              const property: Array<{
                companyId: string;
                companyName: string;
              }> = company.filter((element) => {
                return element.companyName === dataCondition.company;
              });
              getValueHandler(
                property[0].companyId,
                getToday(dateRange[0] ? dateRange[0] : new Date()),
                getToday(dateRange[1] ? dateRange[1] : new Date()),
              );
            }}>
            SELECT
          </Button>
          <Button onClick={() => setIsSelect(false)}>CANCLE</Button>
        </div>
      </div>
      {loading.drawing && (
        <div className="mt-8 font-bold text-xl">
          Drawing now... Please wait!
        </div>
      )}
      {loading.select && (
        <GraphHeader
          className="w-full md:mt-12 mt-4"
          dataCondition={dataCondition}
          dateRange={dateRange}
          valueData={valueData}
        />
      )}
    </>
  );
});
export default ChartMenu;
